// Browser tools: the page the user is on declares tools the assistant can
// call; the backend suspends the turn, this tab runs them against the live
// page and posts the results back. See
// docs/superpowers/specs/2026-10-08-ai-assistant-operates-editor-design.md.

export interface ClientToolDefinition {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface ClientCall {
  id: string;
  tool_name: string;
  args: Record<string, unknown> | null;
}

export interface ClientResult {
  id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
  message?: string;
  /** Images the tool produced (e.g. captured frames): shown to the model. */
  images?: string[];
}

export interface ClientContext {
  path: string;
  tools: ClientToolDefinition[];
  page: Record<string, unknown> | null;
  /** Set only when the site runs inside the mobile app's WebView. */
  surface?: 'ios' | 'android';
}

/** What a page registers so the assistant can see and operate it. */
export interface PageBridge {
  kind: 'editor';
  /** Changes when the page starts showing something else (e.g. another clip). */
  key: string;
  getTools: () => ClientToolDefinition[];
  getState: () => Record<string, unknown>;
  execute: (calls: ClientCall[]) => Promise<{ results: ClientResult[]; undo?: () => void }>;
  /** The frame the page shows now, uploaded (the composer's camera button). */
  captureFrame?: () => Promise<{ url: string; name: string } | null>;
}

export const OPEN_PAGE_TOOL: ClientToolDefinition = {
  name: 'ui_open_page',
  description: 'Open a page of the Autoclipper app for the user and wait until it has loaded, e.g. '
    + '/project/<projectId>/edit (clip editor), /library/<libraryId>/edit, /project/<projectId>/ai-studio '
    + '(AI Studio: preview + chat only), /library/<libraryId>/ai-studio, /project/<projectId>/clips, '
    + '/projects, /library, /community-library, /clips, /earn, /plans. Returns whether an editor is '
    + 'ready; the next step sees the page state. Use documentIds from tools or the page; never invent them.',
  parameters: {
    type: 'object',
    properties: { path: { type: 'string', description: 'App path starting with /' } },
    required: ['path'],
  },
};

// The editor and AI Studio (same editor, preview + chat only).
const EDITOR_PATH = /^\/(project|library)\/[A-Za-z0-9_-]+\/(edit|ai-studio)\/?$/;

export function isEditorPath(path: string): boolean {
  return EDITOR_PATH.test(path);
}

/** Calls in order, with consecutive calls for the same handler grouped so a
 *  page can apply a batch of edits as one change (and one undo). */
export function groupClientCalls(calls: readonly ClientCall[]): Array<{ handler: 'open_page' | 'editor' | 'unknown'; calls: ClientCall[] }> {
  const groups: Array<{ handler: 'open_page' | 'editor' | 'unknown'; calls: ClientCall[] }> = [];
  calls.forEach((call) => {
    let handler: 'open_page' | 'editor' | 'unknown' = 'unknown';
    if (call.tool_name === OPEN_PAGE_TOOL.name) handler = 'open_page';
    else if (call.tool_name.startsWith('editor_')) handler = 'editor';
    const last = groups[groups.length - 1];
    if (last && last.handler === handler && handler !== 'open_page') last.calls.push(call);
    else groups.push({ handler, calls: [call] });
  });
  return groups;
}
